/**
 * DriftView - Semantic drift through the archive
 *
 * "Happy accidents" engine: random walk through latent space.
 * Start from a random node, find neighbors, infinite scroll.
 * Pin to anchor on an interesting node, reseed from there.
 *
 * @module @humanizer/studio/components/archive/DriftView
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useArchiveApi } from '../../hooks/useArchiveApi';
import { useBufferSync } from '../../contexts/BufferSyncContext';
import { getSourceIcon } from '../ui/SourceFilterPills';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

interface DriftNode {
  id: string;
  text: string;
  title?: string;
  sourceType: string;
  sourceCreatedAt?: string;
  wordCount: number;
  threadRootId?: string;
  authorRole?: string;
  score?: number;
}

// ═══════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════

function formatDate(dateStr?: string): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

function truncateText(text: string, maxLen: number): string {
  if (text.length <= maxLen) return text;
  return text.slice(0, maxLen).replace(/\s+\S*$/, '') + '...';
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export function DriftView(): React.ReactElement {
  const archiveApi = useArchiveApi();
  const { openNodeInContext } = useBufferSync();
  const [nodes, setNodes] = useState<DriftNode[]>([]);
  const [pinnedNode, setPinnedNode] = useState<DriftNode | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seenIds = useRef(new Set<string>());

  // Seed the drift with a random node or pinned node
  const seedDrift = useCallback(async (fromNode?: DriftNode) => {
    setLoading(true);
    setError(null);

    try {
      let seedNode = fromNode;

      if (!seedNode) {
        // Get a random embedded node
        const randomResult = await archiveApi.randomNode();
        if (!randomResult) {
          setError('No embedded nodes found. Run embedding from Settings first.');
          setLoading(false);
          return;
        }
        seedNode = randomResult;
      }

      seenIds.current = new Set([seedNode.id]);
      setNodes([seedNode]);

      // Find 5 neighbors
      const text = seedNode.text.slice(0, 2000);
      const similarResult = await archiveApi.findSimilar(text, { limit: 6, threshold: 0.3 });

      const neighbors = similarResult.results
        .filter((r: any) => r.id !== seedNode!.id && !seenIds.current.has(r.id))
        .slice(0, 5)
        .map((r: any) => {
          seenIds.current.add(r.id);
          return {
            id: r.id,
            text: r.text,
            title: r.title,
            sourceType: r.sourceType || 'unknown',
            sourceCreatedAt: r.provenance?.sourceCreatedAt,
            wordCount: r.wordCount || 0,
            threadRootId: r.provenance?.threadRootId,
            authorRole: r.provenance?.authorRole,
            score: r.score,
          };
        });

      setNodes([seedNode, ...neighbors]);
    } catch (err: any) {
      setError(err.message || 'Failed to start drift');
    } finally {
      setLoading(false);
    }
  }, [archiveApi]);

  // Load more: pick a random recent node as next seed
  const loadMore = useCallback(async () => {
    if (loading || nodes.length === 0) return;
    setLoading(true);

    try {
      // Use the last node as seed for the next batch
      const lastNode = nodes[nodes.length - 1];
      const text = lastNode.text.slice(0, 2000);
      const similarResult = await archiveApi.findSimilar(text, { limit: 8, threshold: 0.3 });

      const newNodes = similarResult.results
        .filter((r: any) => !seenIds.current.has(r.id))
        .slice(0, 5)
        .map((r: any) => {
          seenIds.current.add(r.id);
          return {
            id: r.id,
            text: r.text,
            title: r.title,
            sourceType: r.sourceType || 'unknown',
            sourceCreatedAt: r.provenance?.sourceCreatedAt,
            wordCount: r.wordCount || 0,
            threadRootId: r.provenance?.threadRootId,
            authorRole: r.provenance?.authorRole,
            score: r.score,
          };
        });

      if (newNodes.length > 0) {
        setNodes(prev => [...prev, ...newNodes]);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [nodes, loading, archiveApi]);

  // Initial seed
  useEffect(() => {
    seedDrift();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePin = useCallback((node: DriftNode, e: React.MouseEvent) => {
    e.stopPropagation();
    setPinnedNode(node);
    seedDrift(node);
  }, [seedDrift]);

  const handleUnpin = useCallback(() => {
    setPinnedNode(null);
    seedDrift();
  }, [seedDrift]);

  const handleOpenNode = useCallback((node: DriftNode) => {
    const targetId = node.threadRootId || node.id;
    openNodeInContext({ nodeId: targetId });
  }, [openNodeInContext]);

  // Infinite scroll
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [loadMore]);

  return (
    <div className="drift-view">
      {/* Header */}
      <div className="drift-view__header">
        <div className="drift-view__title-row">
          <h3 className="drift-view__title">Drift</h3>
          <button
            className="drift-view__reseed"
            onClick={() => pinnedNode ? seedDrift(pinnedNode) : seedDrift()}
            disabled={loading}
            title="Reseed drift"
          >
            Reseed
          </button>
        </div>
        {pinnedNode && (
          <div className="drift-view__pin-bar">
            <span className="drift-view__pin-label">Pinned:</span>
            <span className="drift-view__pin-title">
              {pinnedNode.title || truncateText(pinnedNode.text, 40)}
            </span>
            <button className="drift-view__pin-clear" onClick={handleUnpin}>Clear</button>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="drift-view__error">{error}</div>
      )}

      {/* Node cards — same pattern as archive browser thread cards */}
      <div className="drift-view__list">
        {nodes.map((node, i) => (
          <button
            key={node.id}
            className="archive-browser__thread-card"
            onClick={() => handleOpenNode(node)}
          >
            <div className="archive-browser__card-header">
              <span className="archive-browser__card-title">
                {node.title || truncateText(node.text, 60)}
              </span>
            </div>
            <div className="archive-browser__card-meta">
              <span className="archive-browser__card-source">
                {getSourceIcon(node.sourceType)} {node.sourceType}
              </span>
              {node.sourceCreatedAt && (
                <>
                  <span className="archive-browser__meta-sep">&middot;</span>
                  <span>{formatDate(node.sourceCreatedAt)}</span>
                </>
              )}
              {node.score != null && i > 0 && (
                <>
                  <span className="archive-browser__meta-sep">&middot;</span>
                  <span>{Math.round(node.score * 100)}% similar</span>
                </>
              )}
            </div>
            {node.text && (
              <div className="archive-browser__card-preview">
                {truncateText(node.text, 120)}
              </div>
            )}
            <div className="archive-browser__card-actions">
              <button
                className="archive-browser__more-like"
                onClick={(e) => handlePin(node, e)}
                title="Pin and reseed from this node"
              >
                Pin
              </button>
            </div>
          </button>
        ))}

        {/* Infinite scroll sentinel */}
        <div ref={sentinelRef} className="drift-view__sentinel">
          {loading && <div className="panel__loading-spinner" />}
        </div>
      </div>
    </div>
  );
}

export default DriftView;
